import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useDivisionRouting } from '../hooks/useDivisionRouting'

interface AccordionSection {
  title: string
  items: Array<{
    label: string
    to?: string
    onClick?: () => void
    disabled?: boolean
  }>
}

export const MobileFooter: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>(null)
  const [isExpanding, setIsExpanding] = useState(false)
  const accordionRef = React.useRef<HTMLDivElement>(null)
  const { navigateToDivision } = useDivisionRouting({
    onDivisionChange: () => { },
    onProductRoute: () => { },
  })

  // Close accordion when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accordionRef.current && !accordionRef.current.contains(event.target as Node) && openSection) {
        setOpenSection(null)
        setTimeout(() => {
          setIsExpanding(false)
        }, 300)
      }
    }

    if (openSection) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside as any)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside as any)
    }
  }, [openSection])

  const toggleSection = (title: string) => {
    const willOpen = openSection !== title

    if (willOpen) {
      // Opening: switch to relative immediately
      setIsExpanding(true)
      setOpenSection(title)
    } else {
      // Closing: keep relative during animation, then switch back
      setOpenSection(null)
      setTimeout(() => {
        setIsExpanding(false)
      }, 300) // Match animation duration
    }
  }

  const sections: AccordionSection[] = [
    {
      title: 'Products',
      items: [
        { label: 'Software', disabled: true },
        { label: 'Hardware', disabled: true },
        { label: 'Services', onClick: () => navigateToDivision('services') },
      ]
    },
    {
      title: 'Company',
      items: [
        { label: 'About', to: '/about' },
        { label: 'Team', to: '/team' },
        { label: 'Careers', to: '/careers' },
        { label: 'Partners', to: '/partners' },
        { label: 'Partnerships', to: '/partnerships' },
      ]
    },
    {
      title: 'Resources',
      items: [
        { label: 'Help Center', to: '/help' },
        { label: 'Contact', to: '/contact' },
      ]
    },
    {
      title: 'Legal',
      items: [
        { label: 'Privacy Policy', to: '/privacy-policy' },
        { label: 'Impressum', to: '/impressum' },
      ]
    }
  ]

  return (
    <footer
      className="relative"
      style={{
        backgroundColor: '#0E0E0E',
        position: (openSection || isExpanding) ? 'relative' : 'sticky',
        bottom: 0,
        zIndex: 1,
        paddingTop: '140px',
        paddingBottom: 'calc(32px + env(safe-area-inset-bottom))',
        marginTop: '-80px'
      }}
    >
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Footer Header with spacing */}
        <div className="text-center pt-12 pb-6">
          <h2
            className="text-xl mb-2"
            style={{
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}
          >
            Build. Innovate. Simplify.
          </h2>
          <p
            className="text-xs"
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 400
            }}
          >
            Explore our ecosystem
          </p>
        </div>

        <div ref={accordionRef} className="mb-8">
          {sections.map((section) => (
            <div
              key={section.title}
              className="border-b"
              style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
            >
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full flex items-center justify-between py-4 transition-all duration-300"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  minHeight: '44px', // Meets 44x44px touch target requirement
                }}
              >
                <span
                  className="text-sm"
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}
                >
                  {section.title}
                </span>
                <motion.svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  animate={{ rotate: openSection === section.title ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
                  style={{ flexShrink: 0 }}
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="rgba(255, 255, 255, 0.5)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              </button>

              <AnimatePresence initial={false}>
                {openSection === section.title && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
                    style={{ overflow: 'hidden', transformOrigin: 'top' }}
                  >
                    <ul className="pb-4 space-y-3">
                      {section.items.map((item) => (
                        <li key={item.label}>
                          {item.disabled ? (
                            <span
                              className="text-sm block"
                              style={{
                                color: 'rgba(255, 255, 255, 0.3)',
                                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                fontWeight: 400,
                                filter: 'blur(1px)',
                                userSelect: 'none',
                                pointerEvents: 'none',
                                paddingLeft: '16px'
                              }}
                            >
                              {item.label}
                            </span>
                          ) : item.onClick ? (
                            <button
                              onClick={item.onClick}
                              className="text-sm block w-full text-left transition-all duration-300"
                              style={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                fontWeight: 400,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px 0 4px 16px',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                              onTouchStart={(e) => {
                                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                              }}
                              onTouchEnd={(e) => {
                                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                              }}
                            >
                              {item.label}
                            </button>
                          ) : (
                            <Link
                              to={item.to || '#'}
                              className="text-sm block transition-all duration-300"
                              style={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                fontWeight: 400,
                                textDecoration: 'none',
                                padding: '4px 0 4px 16px',
                                display: 'block'
                              }}
                              onTouchStart={(e) => {
                                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                              }}
                              onTouchEnd={(e) => {
                                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'
                              }}
                            >
                              {item.label}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img
              src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
              alt="Mernaki Logo"
              className="object-contain"
              style={{ height: '36px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
              loading="lazy"
            />
            <h3
              className="text-base"
              style={{
                color: '#FFFFFF',
                fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                fontWeight: 400
              }}
            >
              Mernaki
            </h3>
          </div>
          <p
            className="text-xs leading-relaxed mb-6"
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 400
            }}
          >
            Building intelligent systems that power global innovation.
          </p>
        </div>

        <div className="mb-8 pb-8 border-b border-white/10">
          <div className="flex items-center justify-center gap-2 flex-wrap text-xs">
            <span
              style={{
                color: 'rgba(255, 255, 255, 0.3)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                filter: 'blur(1.5px)',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            >
              Project Crimson
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
            <span
              style={{
                color: 'rgba(255, 255, 255, 0.3)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                filter: 'blur(1.5px)',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            >
              Project Velocity
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
            <span
              style={{
                color: 'rgba(255, 255, 255, 0.3)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                filter: 'blur(1.5px)',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            >
              Project Nexus
            </span>
          </div>
        </div>

        <div className="text-center space-y-3">
          <div className="flex flex-col items-center gap-2 text-xs">
            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Munich, Germany</span>
            <a
              href="mailto:info@mernaki.de"
              className="transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.4)',
                textDecoration: 'none'
              }}
            >
              Info@mernaki.de
            </a>
          </div>
          <p
            className="text-xs"
            style={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 300
            }}
          >
            © {new Date().getFullYear()} Mernaki. Crafted in Germany.
          </p>
        </div>
      </div>
    </footer>
  )
}
